import { Answer } from "@coffee/data-access/api-types";
import { UnlikelyAISpinner } from "@coffee/ui/components/spinners";
import { Timeline, TimelineStep } from "@coffee/ui/components/timeline";
import styled from "@emotion/styled";
import { Paragraph } from "@unlikelyai-magic/ui/typography";
import { Citations } from "./Citations";
import { MarkdownViewer } from "./MarkdownViewer";

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.background[1]};
  border-top-right-radius: ${({ theme }) => theme.roundness.lg};
  border-bottom-right-radius: ${({ theme }) => theme.roundness.lg};
  justify-content: center;
  align-items: center;
`;

const ExplanationContainer = styled.div`
  width: 70%;
  height: 100%;
  padding-top: ${({ theme }) => theme.spacings["3xl"]};
  padding-bottom: ${({ theme }) => theme.spacings["3xl"]};
  overflow-y: auto;
`;

const SpinnerContainer = styled.div`
  height: 20%;
  width: 20%;
`;

const ExplanationText = styled(Paragraph)`
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: ${({ theme }) => theme.lineHeights.sm};
`;

const MarkdownWrapper = styled.div`
  font-weight: bold;
  white-space: pre-wrap;
`;

type ExplanationViewProps = {
  answer: Answer | null;
  isLoading?: boolean;
};

export const ExplanationView = ({
  isLoading,
  answer,
}: ExplanationViewProps) => {
  const showSpinner = isLoading || !answer;

  if (showSpinner) {
    return (
      <Container>
        <SpinnerContainer>
          <UnlikelyAISpinner />
        </SpinnerContainer>
      </Container>
    );
  }

  const hasCitations = answer.citations && answer.citations.length > 0;

  const steps = [
    <TimelineStep key="short_answer" title="Response sent to user">
      <ExplanationText>{answer.short_answer}</ExplanationText>
    </TimelineStep>,
    <TimelineStep key="explanation" title="Explanation">
      <MarkdownWrapper>
        <MarkdownViewer content={answer.answer} />
      </MarkdownWrapper>
    </TimelineStep>,
  ];
  if (hasCitations) {
    steps.push(
      <TimelineStep
        key="citations"
        title="The clauses required to get to an answer which will be cited in the response. You can select a clause to understand how it is being used in the answer."
      >
        <Citations citations={answer.citations} />
      </TimelineStep>
    );
  }

  return (
    <Container>
      <ExplanationContainer>
        <Timeline>{steps}</Timeline>
      </ExplanationContainer>
    </Container>
  );
};
